import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { listReportsEndpoint } from "@visioni/a3s-reports/client-messages";
import type { ListReportsResponse, ReportListItem } from "@visioni/a3s-reports/client-messages";
import { useTenant, useTenantId } from "@src/features/tenants";

/**
 * Lists the tenant's RoadSense reports newest-first — an **independent** copy
 * of the WasteSense listing hook (not shared) that leans only on the generic
 * endpoint + auth/tenant plumbing, with `type: "roadsense"`.
 *
 * Returns **all** statuses (no `status` filter). We deliberately surface
 * `generating`/`failed` rows so a report that's mid-(re)generation isn't
 * silently dropped — otherwise a tenant whose only report is regenerating sees
 * a misleading "No reports yet". The site list keeps a lifecycle's sites
 * clickable at any status, since the live site report serves a regenerating
 * report; only a lifecycle with no per-site list yet (never generated) renders
 * as a non-clickable placeholder with a status-aware sub-label.
 *
 * Pagination is cursor-based: the endpoint forwards the DDB `LastEvaluatedKey`
 * as an opaque base64 `cursor`, which `useInfiniteQuery` threads through
 * subsequent fetches. Cursor-based (vs offset) keeps the list stable when the
 * generator stamps a new row at the front mid-scroll.
 */
const STALE_TIME_MS = 60_000;

/**
 * Page size. The endpoint caps at 100; 52 keeps a year's worth of weekly rows
 * on a single page so steady-state tenants never have to "Load more".
 */
const PAGE_LIMIT = 52;

export interface UseRoadSenseReportListResult {
  readonly reports: readonly ReportListItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly isTenantPending: boolean;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
}

export function useRoadSenseReportList(): UseRoadSenseReportListResult {
  const apiConfig = useApiConfig();
  const tenantId = useTenantId();
  const { isLoading: isTenantLoading } = useTenant();

  const tenant = apiConfig.tenant;
  const enabled = !!tenant;

  const query = useInfiniteQuery<
    ListReportsResponse,
    Error,
    InfiniteData<ListReportsResponse, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: ["roadsense", "reports-list", tenant?.tenantId, "all", PAGE_LIMIT] as const,
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.cursor,
    queryFn: async ({ pageParam, signal }) => {
      // `enabled` gates the call; this is a defensive narrow for the type system
      // since `tenant` is optional on `ApiConfig`.
      if (!tenant) throw new Error("Tenant not ready");
      const path = { tenantId: tenant.tenantId, type: "roadsense" as const };
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listReportsEndpoint, {
          path,
          // No `status` filter: we want generating/failed rows too so the list
          // never falsely reads as empty during a (re)generation.
          query: { limit: PAGE_LIMIT, ...(pageParam !== undefined && { cursor: pageParam }) },
        });

      const response = await apiConfig.authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) message = json.message;
              else message = text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new Error(message);
      }
      const data: unknown = await response.json();
      return listReportsEndpoint.responseSchema.parse(data);
    },
  });

  const reports = useMemo<readonly ReportListItem[]>(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return {
    reports,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error instanceof Error ? query.error : null,
    isTenantPending: isTenantLoading || !tenantId,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
